import { ReactNode, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import UIcon from "lib/icons/untitled_ui/UIcon";

type ExpandableContentProps = {
  maxLines: number;
  lineHeight: number;
  children: ReactNode;
};

export default function ExpandableContent({
  maxLines,
  lineHeight,
  children,
}: ExpandableContentProps) {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      setIsOverflowing(el.scrollHeight > el.clientHeight);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const showToggle = isOverflowing || expanded;

  return (
    <>
      <Content
        ref={contentRef}
        expanded={expanded}
        isOverflowing={isOverflowing}
        maxHeight={lineHeight * maxLines}
      >
        {children}
      </Content>
      {showToggle && (
        <ToggleContainer>
          <Button buttonType="tertiary" onClick={() => setExpanded((prev) => !prev)}>
            <Text color="sysTextDefault">{expanded ? "View less" : "View more"}</Text>
            <UIcon icon={expanded ? "chevronUp" : "chevronDown"} size={16} color="sysTextDefault" />
          </Button>
        </ToggleContainer>
      )}
    </>
  );
}

const Content = styled.div<{ expanded: boolean; isOverflowing: boolean; maxHeight: number }>(
  ({ expanded, isOverflowing, maxHeight, theme }) => ({
    position: "relative",
    overflow: "hidden",
    maxHeight: expanded ? undefined : maxHeight,
    ...(!expanded &&
      isOverflowing && {
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 48,
          background: `linear-gradient(to bottom, transparent, ${theme.colors.sysBackgroundDefault})`,
          pointerEvents: "none",
        },
      }),
  }),
);

const ToggleContainer = styled.div({
  display: "flex",
  justifyContent: "center",
  padding: "4px 0 8px",
});
